import {
  ModuleTypeEnum,
  UserPreferenceData,
  PreferenceContextEnum,
  PreferenceScopeEnum
} from '@scrambl/shared';
import { apiClient } from './api-client';
import { createSingletonApi } from './singleton-factory';
import { createLogger } from '@/lib/logger';

const logger = createLogger('lib/api/user-preferences.api');

export class UserPreferencesApi {
  // Get user preferences (singular)
  async getUserPreferences(
    context: PreferenceContextEnum,
    moduleType?: ModuleTypeEnum,
    scope?: PreferenceScopeEnum,
    options?: { organizationId?: string | null },
  ): Promise<UserPreferenceData | null> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('context', context);
      if (moduleType) {
        queryParams.append('moduleType', moduleType);
      }
      if (scope) {
        queryParams.append('scope', scope);
      }

      const response = await apiClient<{ data: UserPreferenceData | null; message: string }>(
        `/user-preferences?${queryParams.toString()}`,
        options?.organizationId
          ? {
              skipOrgContext: true,
              headers: { 'x-organization-id': options.organizationId },
            }
          : undefined,
      );

      return response.data;
    } catch (error) {
      logger.error('[UserPreferencesApi] Error fetching user preferences:', error);
      throw error;
    }
  }

  // Get ALL user preferences for an organization
  async getAllUserPreferences(options?: {
    organizationId?: string | null
  }): Promise<{ data: UserPreferenceData[]; message: string }> {
    try {
      const response = await apiClient<{ data: UserPreferenceData[]; message: string }>(
        `/user-preferences/all`,
        options?.organizationId
          ? {
              skipOrgContext: true,
              headers: { 'x-organization-id': options.organizationId },
            }
          : undefined,
      );

      return response;
    } catch (error) {
      logger.error('[UserPreferencesApi] Error fetching all user preferences:', error);
      throw error;
    }
  }

  // Save user preferences
  async saveUserPreferences(
    context: PreferenceContextEnum,
    preferences: Record<string, unknown>,
    moduleType?: ModuleTypeEnum,
    scope: PreferenceScopeEnum = PreferenceScopeEnum.USER,
    options?: { organizationId?: string | null },
  ): Promise<UserPreferenceData> {
    try {
      const response = await apiClient<{ data: UserPreferenceData; message: string }>(
        '/user-preferences',
        {
          method: 'POST',
          skipOrgContext: Boolean(options?.organizationId),
          headers: options?.organizationId
            ? { 'x-organization-id': options.organizationId }
            : undefined,
          body: {
            context,
            moduleType,
            scope,
            preferences,
          },
        }
      );

      return response.data;
    } catch (error) {
      logger.error('[UserPreferencesApi] Error saving user preferences:', error);
      throw error;
    }
  }

  // Reset user preferences to defaults
  async resetUserPreferences(
    context: PreferenceContextEnum,
    moduleType?: ModuleTypeEnum,
  ): Promise<void> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('context', context);
      if (moduleType) {
        queryParams.append('moduleType', moduleType);
      }

      await apiClient(
        `/user-preferences/reset?${queryParams.toString()}`,
        {
          method: 'POST',
        }
      );
    } catch (error) {
      logger.error('[UserPreferencesApi] Error resetting user preferences:', error);
      throw error;
    }
  }
}

// Export a singleton instance
export const userPreferencesApi = createSingletonApi(UserPreferencesApi);
